import { useEffect, useRef, useState } from "react";
import { AppIcon, type AppIconName } from "../AppIcon";
import { ApiError, type ApiClient } from "../../api/client";
import { prepare, resend } from "../../api/pending";
import type {
  BillAnswer,
  BillRequest,
  GeneralSettings,
  LineRequest,
  Order,
  PaymentMethod,
  PaymentRequest,
  QrPayment,
  SplitResult,
  StaffMember,
} from "../../api/types";
import { t } from "../../i18n";
import { formatEuro, type Cents } from "../../lib/money";
import { QrCode } from "../../ui/QrCode";
import { BankQrPanel } from "../BankQrPanel";
import {
  assignedUnits,
  epcQr,
  equalShare,
  guestOf,
  guestsOnTable,
  nextGuestNumber,
  kitchenCopies,
  pickedAmount,
  pickedEverything,
  printingPc,
  receiptPrinter,
  TIP_PERCENTS,
  tipOf,
  type Guest,
  type Spot,
} from "./waiter";

type Mode = "all" | "items" | "equal";
/** The buttons of the prototype; Tap to Pay and the Sunmi's reader wait for the payment provider. */
/**
 * "qr": the bank-QR on the screen; "qrBill": Bancontact Pro QR printed on the bill or the receipt. Two
 * buttons of their own (Furkan, 10/10/2026: "de ober moet kunnen kiezen tussen de 2, apart in de
 * betaalmethodes"): the choice inside the QR could no longer be reached once the QR was on the screen.
 */
type MethodKey = "cash" | "qr" | "qrBill" | "tap" | "sunmi" | "terminal";

/**
 * Tap to Pay and the card reader of the Sunmi come with the payment provider (after the pilot). Until then
 * they are not shown at all (08/10/2026, before the stores: Apple and Google refuse buttons that do not work
 * yet); the prototype showed them greyed out with a note.
 */
const providerReady = (): boolean => false;

const METHOD: Record<MethodKey, PaymentMethod> = {
  cash: "cash",
  qr: "bank_qr",
  qrBill: "bank_qr",
  // a card on the loose terminal, Tap to Pay or the Sunmi: booked as Bancontact (prototype PMN)
  tap: "bancontact",
  sunmi: "bancontact",
  terminal: "bancontact",
};

const METHOD_ICON: Record<MethodKey, AppIconName> = {
  cash: "cash",
  qr: "qr",
  qrBill: "bill",
  tap: "tap",
  sunmi: "card",
  terminal: "terminal",
};

interface Props {
  api: ApiClient;
  spot: Spot;
  /** The open order of the table (everything sent to the kitchen first); null for the takeaway. */
  order: Order | null;
  /** The takeaway: the lines not sent yet, the customer's name and what they cost (null while priced). */
  /** The takeaway: its lines, the name for the messages, and the customer and note as the order sends them. */
  takeaway: {
    lines: LineRequest[];
    name: string;
    customer?: Record<string, string>;
    note?: string;
    totalCents: Cents | null;
  } | null;
  general: GeneralSettings | null;
  /** The shop has a key of Bancontact Pro: the bank-QR is made by Bancontact and confirms itself (ADR 0024). */
  bankQrAutomatic: boolean;
  /** The shop has Bancontact's product "On a Receipt": the QR can go on the bill or the receipt (ADR 0029). */
  receiptQr: boolean;
  /** The bill was printed with a QR or a verdeling; `leave`: back to the tables, nothing more to do here. */
  onBill: (answer: BillAnswer, leave: boolean) => void;
  staff: StaffMember;
  /** sunmi, iphone, ipad, android or tablet: which card buttons the prototype shows. */
  deviceType: string;
  onBack: () => void;
  /** An order changed or was made: in the list at once (the live update follows). */
  onOrder: (order: Order) => void;
  /** Everything is paid: back to the tables or the takeaway. */
  onDone: () => void;
  toast: (message: string, tone?: "info" | "bad") => void;
  report: (error: unknown) => void;
}

/** Where the receipt went: "via hoofdcomputer (Kassa PC 1) → Kassaprinter toog" (prototype obPrn). */
function receiptWhere(order: Order, kind: "receipt" | "bill" = "receipt"): string | null {
  const printed = receiptPrinter(order.prints, kind);
  if (printed === null) return null;
  return `${t.tafoApp.waiter.viaPc(printingPc(order.prints))} → ${printed.printer}`;
}

/**
 * Afrekenen in the app (prototype obPayHtml, obFinishPay): at a table Alles, Per product or Gelijk
 * verdelen, the tip (Geen, 5, 10, 15%), then the method; cash, bank-QR and the loose terminal are
 * confirmed with one button. Every payment is sent once (idempotent) and the receipt prints at the end.
 *
 * In a shop with a key of Bancontact Pro the bank-QR confirms itself (ADR 0024): the QR is Bancontact's,
 * for exactly the amount on the screen, and the payment is booked the moment Bancontact says it is paid.
 * While that QR is shown, nothing else on the screen can change the amount or lead away from it.
 */
export function PayView({
  api,
  spot,
  order,
  takeaway,
  general,
  bankQrAutomatic,
  receiptQr,
  onBill,
  staff,
  deviceType,
  onBack,
  onOrder,
  onDone,
  toast,
  report,
}: Props) {
  const text = t.tafoApp.pay;
  const table = spot.kind === "table" ? spot.table : null;
  const splitRunning = order?.split ?? null;
  const [mode, setMode] = useState<Mode>(splitRunning ? "equal" : "all");
  const [picked, setPicked] = useState<ReadonlyMap<number, number>>(new Map());
  const [persons, setPersons] = useState(splitRunning?.persons ?? 2);
  const [tip, setTip] = useState<number>(0);
  const [method, setMethod] = useState<MethodKey | null>(null);
  const [busy, setBusy] = useState(false);
  /** Bancontact has the money and booking it failed for good: said under the QR. */
  const [notBooked, setNotBooked] = useState(false);
  /** "Per product": the guests already on the bill, each with what he had (Furkan, 09/10/2026). */
  const [guests, setGuests] = useState<Guest[]>([]);

  const person = (order?.split?.paid ?? 0) + 1;
  const fixedPersons = order?.split?.persons ?? persons;
  const amount: Cents | null =
    takeaway !== null
      ? takeaway.totalCents
      : order === null
        ? null
        : mode === "all"
          ? order.totalCents - order.paidCents
          : mode === "items"
            ? pickedAmount(order, picked)
            : equalShare(order.totalCents, order.paidCents, fixedPersons, person);
  const tipOn = table !== null && general?.tips === true;
  const tipCents = tipOn && amount !== null ? tipOf(amount, tip) : 0;
  const due = (amount ?? 0) + tipCents;
  const itemsBlocked = order !== null && (order.discountCents > 0 || order.split !== null);

  const accepted = new Set(staff.acceptedPaymentMethods);
  const screenQr = general !== null && general.bankQr.waiter && (bankQrAutomatic || general.iban !== "");
  /** Bancontact's QR is on the screen: the amount is fixed and the only way out is the one below the QR. */
  const qrShown = bankQrAutomatic && method === "qr";
  /** The guests on the bill as lines of the table now, and how many units of each line they have. */
  const guestLines = order === null ? [] : guestsOnTable(order, guests);
  const assigned = assignedUnits(guestLines);
  const sunmi = deviceType === "sunmi";
  const tablet = deviceType === "ipad" || deviceType === "tablet";
  const methods: MethodKey[] = [
    "cash",
    ...(screenQr ? (["qr"] as const) : []),
    ...(receiptQr ? (["qrBill"] as const) : []),
    ...(!providerReady() ? [] : sunmi ? (["sunmi"] as const) : tablet ? [] : (["tap"] as const)),
    "terminal",
  ];
  const shown = methods.filter((key) => accepted.has(METHOD[key]));

  const reset = () => {
    setMethod(null);
    setTip(0);
    setPicked(new Map());
  };
  const tipText = (cents: Cents) => (cents > 0 ? text.tipPart(formatEuro(cents)) : "");

  /** Sends a sale or a payment exactly once, also after a lost connection (ADR 0006). */
  async function send<T>(path: string, body: unknown, label: string): Promise<T> {
    return resend<T>(api, prepare(path, body, label));
  }

  /** `confirmed`: the bank-QR that Bancontact says is paid, for exactly what is on the screen. */
  const pay = async (confirmed?: QrPayment) => {
    if (method === null || amount === null || amount <= 0 || busy) return;
    const payment: PaymentRequest = {
      method: METHOD[method],
      amountCents: amount,
      ...(tipCents > 0 && { tipCents }),
      ...(confirmed !== undefined && { qrPaymentId: confirmed.id }),
    };
    setBusy(true);
    try {
      if (takeaway !== null) {
        // "💶 Nu afrekenen": the takeaway is made and paid at once (kitchen, receipt)
        const made = await send<Order>(
          "/orders",
          {
            channel: "takeaway",
            lines: takeaway.lines,
            ...(takeaway.customer && { customer: takeaway.customer }),
            ...(takeaway.note !== undefined && { note: takeaway.note }),
            payments: [payment],
          },
          `${t.tafoApp.waiter.takeaway} · ${formatEuro(amount)}`,
        );
        onOrder(made);
        if (made.status === "paid") {
          const kitchen = kitchenCopies(made.prints) ? text.kitchenCopy : text.kitchenTicket;
          toast(text.takeawayPaid(made.code, kitchen, receiptWhere(made) ?? t.tafoApp.waiter.noPrinter));
        } else {
          toast(text.takeawayOpen(made.code), "bad");
        }
        onDone();
        return;
      }
      if (order === null || table === null) return;
      const label = `${t.tafoApp.waiter.table(table)} · ${formatEuro(amount)}`;
      if (mode === "items" && !pickedEverything(order, picked)) {
        // "Per product": the guest's part becomes its own paid order, the table keeps the rest
        const result = await send<SplitResult>(
          `/orders/${order.id}/split`,
          {
            expectedRevision: order.revision,
            lines: [...picked].filter(([, quantity]) => quantity > 0).map(([index, quantity]) => ({ index, quantity })),
            payment,
          },
          label,
        );
        onOrder(result.order);
        onOrder(result.table);
        toast(
          text.paidPart(
            table,
            formatEuro(result.order.totalCents),
            tipText(tipCents),
            receiptWhere(result.order) ?? t.tafoApp.waiter.noPrinter,
          ),
        );
        reset();
        return;
      }
      const equal = mode === "equal";
      const paid = await send<Order>(
        `/orders/${order.id}/payments`,
        { payments: [payment], ...(equal && { split: { persons: fixedPersons } }) },
        label,
      );
      onOrder(paid);
      if (paid.status !== "paid") {
        toast(text.personPaid(person, fixedPersons, formatEuro(amount)));
        reset();
        return;
      }
      const where = receiptWhere(paid) ?? t.tafoApp.waiter.noPrinter;
      toast(
        equal
          ? text.paidEqual(table, fixedPersons, tipText(paid.tipCents), where)
          : text.paidAll(table, formatEuro(paid.totalCents), tipText(tipCents), where),
      );
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.retryable) {
        // still pending: the app sends it again by itself, so it must not be paid a second time
        toast(text.deferred, "bad");
        onDone();
      } else {
        if (confirmed !== undefined) setNotBooked(true);
        report(error);
      }
    } finally {
      setBusy(false);
    }
  };

  // A bank-QR of this table that was paid while nobody looked (the phone went dark at that moment):
  // booked as soon as the waiter opens the payment of the table again.
  const orderId = order?.id ?? null;
  const looked = useRef<string | null>(null);
  useEffect(() => {
    if (!bankQrAutomatic || orderId === null || looked.current === orderId) return;
    looked.current = orderId;
    void (async () => {
      try {
        const open = await api.request<{ qrPayments: QrPayment[] }>("GET", `/device/qr-payments?orderId=${orderId}`);
        for (const found of open.qrPayments.filter((qr) => qr.status === "succeeded" && !qr.booked)) {
          const paid = await resend<Order>(
            api,
            prepare(
              `/orders/${orderId}/payments`,
              {
                payments: [
                  {
                    method: "bank_qr",
                    amountCents: found.amountCents - found.tipCents,
                    ...(found.tipCents > 0 && { tipCents: found.tipCents }),
                    qrPaymentId: found.id,
                  },
                ],
              },
              `${found.label} · ${formatEuro(found.amountCents)}`,
            ),
          );
          onOrder(paid);
          toast(text.qrFound(formatEuro(found.amountCents)));
          if (paid.status === "paid") onDone();
        }
      } catch (error) {
        report(error);
      }
    })();
  }, [api, bankQrAutomatic, orderId, onOrder, onDone, toast, report, text]);

  const pick = (index: number, delta: number) => {
    if (order === null) return;
    const line = order.lines[index];
    if (!line) return;
    const next = new Map(picked);
    const free = line.quantity - (assigned.get(index) ?? 0);
    next.set(index, Math.max(0, Math.min(free, (picked.get(index) ?? 0) + delta)));
    setPicked(next);
  };

  /** "🧾 Rekening" with one QR for all, or the verdeling; printed on the receipt printer of the tables. */
  const printBill = async (request: BillRequest, leave: boolean) => {
    if (order === null || busy) return;
    setBusy(true);
    try {
      onBill(await api.request<BillAnswer>("POST", `/orders/${order.id}/bill`, { body: request }), leave);
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  /** The guests on the bill, with the one being picked now when he wants his own QR too. */
  const printSplit = (withPicked: boolean) => {
    if (order === null) return;
    const all =
      withPicked && pickedAmount(order, picked) > 0
        ? [...guests, guestOf(order, picked, true, nextGuestNumber(guests))]
        : guests;
    if (withPicked) {
      setGuests(all);
      setPicked(new Map());
    }
    // a guest who paid or whose lines are gone is no longer on the bill
    const lines = guestsOnTable(order, all).flatMap((map, index) =>
      map === null || map === "paid"
        ? []
        : [
            {
              number: all[index]!.number,
              lines: [...map].map(([line, quantity]) => ({ index: line, quantity })),
              qr: all[index]!.qr,
            },
          ],
    );
    if (lines.length === 0) return;
    void printBill({ expectedRevision: order.revision, split: { kind: "items", guests: lines } }, false);
  };

  /** "Op de bon" for the takeaway: made unpaid, its receipt printed with the QR (ADR 0029). */
  const takeawayOnReceipt = async () => {
    if (takeaway === null || amount === null || busy) return;
    setBusy(true);
    try {
      const made = await send<Order>(
        "/orders",
        {
          channel: "takeaway",
          lines: takeaway.lines,
          ...(takeaway.customer && { customer: takeaway.customer }),
          ...(takeaway.note !== undefined && { note: takeaway.note }),
          receiptQr: true,
        },
        `${t.tafoApp.waiter.takeaway} · ${formatEuro(amount)}`,
      );
      onOrder(made);
      toast(text.takeawayQr(made.code, formatEuro(made.totalCents)));
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.retryable) {
        toast(text.deferred, "bad");
        onDone();
      } else {
        report(error);
      }
    } finally {
      setBusy(false);
    }
  };

  const qrText = () =>
    epcQr(
      general === null ? "" : general.company !== "" ? general.company : general.displayName,
      general?.iban ?? "",
      due,
      text.reference(table === null ? t.tafoApp.waiter.takeaway.toLowerCase() : `tafel ${table}`),
    );

  return (
    <>
      <button type="button" className="linkbtn" disabled={qrShown} onClick={onBack}>
        {t.tafoApp.waiter.back}
      </button>
      <h3>{text.title(table)}</h3>
      {order !== null && (
        <div className="seg ob-seg" role="group" aria-label={text.title(table)}>
          {(["all", "items", "equal"] as const).map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={mode === key}
              disabled={busy || qrShown || (key === "items" && itemsBlocked)}
              onClick={() => {
                // the method stays chosen (prototype data-om): the amount follows the new way
                setMode(key);
                setPicked(new Map());
              }}
            >
              {text.modes[key]}
            </button>
          ))}
        </div>
      )}
      {order !== null && itemsBlocked && mode !== "items" && (
        <p className="proto ob-hint">{order.split !== null ? text.itemsWhileSplit : text.itemsWithDiscount}</p>
      )}
      {order !== null && mode === "items" && (
        <>
          <p className="proto ob-hint">{text.itemsHint}</p>
          {order.lines.map((line, index) => {
            const count = picked.get(index) ?? 0;
            const name = `${line.name}${line.variant === null ? "" : ` ${line.variant}`}`;
            return (
              <div className="ob-line" key={index}>
                <div>
                  <b>{name}</b>
                  <small>{text.perPiece(line.free ? t.receipt.free : formatEuro(line.unitCents), line.quantity)}</small>
                </div>
                <div className="qty">
                  <button
                    type="button"
                    aria-label={`${t.tafoApp.waiter.less} ${name}`}
                    disabled={qrShown || count === 0}
                    onClick={() => {
                      pick(index, -1);
                    }}
                  >
                    −
                  </button>
                  <output>{count}</output>
                  <button
                    type="button"
                    aria-label={`${t.tafoApp.waiter.more} ${name}`}
                    disabled={
                      qrShown || count >= line.quantity - (assigned.get(index) ?? 0) || line.request?.promotion === true
                    }
                    onClick={() => {
                      pick(index, 1);
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            className="btn"
            disabled={busy || qrShown || pickedAmount(order, picked) === 0}
            onClick={() => {
              setGuests([...guests, guestOf(order, picked, false, nextGuestNumber(guests))]);
              setPicked(new Map());
            }}
          >
            {text.split.addGuest(nextGuestNumber(guests))}
          </button>
          {guests.length > 0 && (
            <div className="ob-guests" role="list" aria-label={text.split.print}>
              {guests.map((guest, index) => {
                const lines = guestLines[index] ?? null;
                const { number } = guest;
                const paid = lines === "paid";
                const open = lines === null || paid ? null : lines;
                return (
                  <div className="ob-guest" role="listitem" key={index}>
                    <span>
                      <b>{text.split.guest(number)}</b>{" "}
                      {paid ? (
                        <small className="ok">{text.split.paid}</small>
                      ) : open === null ? (
                        <small>{text.split.gone}</small>
                      ) : (
                        <span className="num">{formatEuro(pickedAmount(order, open))}</span>
                      )}
                    </span>
                    {receiptQr && !paid && (
                      <button
                        type="button"
                        className="chip"
                        aria-pressed={guest.qr}
                        title={text.split.qrTitle}
                        disabled={busy || open === null}
                        onClick={() => {
                          setGuests(guests.map((item, at) => (at === index ? { ...item, qr: !item.qr } : item)));
                        }}
                      >
                        <AppIcon name="qr" />
                        {text.split.qr}
                      </button>
                    )}
                    {!paid && (
                      <button
                        type="button"
                        className="btn"
                        disabled={busy || qrShown || open === null}
                        onClick={() => {
                          // this guest pays now: his lines are picked, he leaves the bill
                          setPicked(open ?? new Map());
                          setGuests(guests.filter((_, at) => at !== index));
                        }}
                      >
                        {text.split.payNow}
                      </button>
                    )}
                    <button
                      type="button"
                      className="linkbtn"
                      aria-label={text.split.remove(number)}
                      disabled={busy}
                      onClick={() => {
                        setGuests(guests.filter((_, at) => at !== index));
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn primary"
                disabled={busy || qrShown}
                onClick={() => {
                  printSplit(false);
                }}
              >
                {text.split.print}
              </button>
            </div>
          )}
          {guests.length === 0 && <p className="proto ob-hint">{text.split.hint}</p>}
        </>
      )}
      {order !== null && mode === "equal" && (
        <>
          <div className="ob-row ob-persons">
            <span>{text.persons}</span>
            <div className="qty">
              <button
                type="button"
                aria-label={`${t.tafoApp.waiter.less} ${text.persons}`}
                disabled={qrShown || splitRunning !== null || persons <= 2}
                onClick={() => {
                  setPersons(persons - 1);
                }}
              >
                −
              </button>
              <output>{fixedPersons}</output>
              <button
                type="button"
                aria-label={`${t.tafoApp.waiter.more} ${text.persons}`}
                disabled={qrShown || splitRunning !== null || persons >= 20}
                onClick={() => {
                  setPersons(persons + 1);
                }}
              >
                +
              </button>
            </div>
          </div>
          <p className="proto ob-hint">
            {text.personOf(person, fixedPersons, formatEuro(equalShare(order.totalCents, 0, fixedPersons, 1)))}
          </p>
          <button
            type="button"
            className="btn"
            disabled={busy || qrShown}
            onClick={() =>
              void printBill(
                { expectedRevision: order.revision, split: { kind: "equal", persons: fixedPersons, qr: false } },
                false,
              )
            }
          >
            {text.split.printEqual}
          </button>
        </>
      )}
      <div className="ob-tot">
        <span>{order === null || mode === "all" ? text.total : text.nowDue}</span>
        <b className="num">{amount === null ? "…" : formatEuro(amount)}</b>
      </div>
      {tipOn && amount !== null && amount > 0 && (
        <>
          <div className="ob-tip">
            <span>{text.tip}</span>
            <div className="seg ob-seg" role="group" aria-label={text.tip}>
              {TIP_PERCENTS.map((percent) => (
                <button
                  key={percent}
                  type="button"
                  aria-pressed={tip === percent}
                  disabled={qrShown}
                  onClick={() => {
                    setTip(percent);
                  }}
                >
                  {percent === 0 ? text.tipNone : `${String(percent)}%`}
                </button>
              ))}
            </div>
          </div>
          {tipCents > 0 && (
            <>
              <div className="ob-tipsum">
                <span>{text.tipLine(tip)}</span>
                <b className="num">{formatEuro(tipCents)}</b>
              </div>
              <div className="ob-tipsum">
                <span>
                  <b>{text.toPay}</b>
                </span>
                <b className="num">{formatEuro(due)}</b>
              </div>
            </>
          )}
        </>
      )}
      <div className="ob-pm" role="group" aria-label={text.methodsLabel}>
        {shown.map((key) => (
          <button
            key={key}
            type="button"
            className="btn"
            aria-pressed={method === key}
            disabled={busy || qrShown || amount === null || amount <= 0}
            onClick={() => {
              setMethod(key);
            }}
          >
            <AppIcon name={METHOD_ICON[key]} />
            {text.methods[key]}
          </button>
        ))}
      </div>
      {method !== null && amount !== null && amount > 0 && (
        <div className="ob-pmdet">
          {method === "cash" && (
            <button type="button" className="btn primary ph-big" disabled={busy} onClick={() => void pay()}>
              <AppIcon name="check" />
              {text.cashDone}
            </button>
          )}
          {method === "qrBill" && (
            <>
              {tipCents > 0 && <p className="proto ob-hint">{text.qrBillNoTip}</p>}
              <button
                type="button"
                className="btn primary ph-big"
                disabled={busy}
                onClick={() => {
                  if (takeaway !== null) void takeawayOnReceipt();
                  else if (order !== null && mode === "all")
                    void printBill({ expectedRevision: order.revision, qr: true }, true);
                  else if (order !== null && mode === "equal")
                    void printBill(
                      { expectedRevision: order.revision, split: { kind: "equal", persons: fixedPersons, qr: true } },
                      false,
                    );
                  else printSplit(true);
                }}
              >
                <AppIcon name="qr" />
                {takeaway !== null
                  ? text.qrBillTakeaway
                  : mode === "all"
                    ? text.qrBillAll
                    : mode === "equal"
                      ? text.qrBillEqual(fixedPersons)
                      : text.qrBillGuest}
              </button>
              <p className="proto ob-hint c">{text.qrBillHint}</p>
            </>
          )}
          {method === "qr" && bankQrAutomatic && (
            <BankQrPanel
              api={api}
              amountCents={due}
              tipCents={tipCents}
              orderId={order?.id ?? null}
              scanText={text.qrScan}
              onPaid={(qr) => void pay(qr)}
              booking={busy}
              notBooked={notBooked}
              onLeave={() => {
                setNotBooked(false);
                setMethod(null);
              }}
              leaveText={t.tafoApp.bankQr.other}
            />
          )}
          {method === "qr" && !bankQrAutomatic && (
            <>
              <p className="ob-qrhead">
                {text.qrScan}
                <br />
                <b className="num">{formatEuro(due)}</b>
              </p>
              <div className="ph-qr sm">
                <QrCode text={qrText()} label={text.qrScan} />
              </div>
              <p className="proto ob-hint c">{text.qrCheck}</p>
              <button type="button" className="btn primary ph-big" disabled={busy} onClick={() => void pay()}>
                <AppIcon name="check" />
                {text.qrDone}
              </button>
            </>
          )}
          {method === "terminal" && (
            <>
              <p className="c">{text.terminalType(formatEuro(due))}</p>
              <button type="button" className="btn primary ph-big" disabled={busy} onClick={() => void pay()}>
                <AppIcon name="check" />
                {text.terminalDone}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
